import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getPayInOutList } from "Store/Reducer/common.slice";
import { formatUiDateTimeParts } from "utils/dateFormats";
import useAppLanguage from "hooks/useAppLanguage";

const formatAmount = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) return "—";
  const suffixMatch = raw.match(/\s*([A-Za-z]+)\s*$/);
  const suffix = suffixMatch?.[1] ?? "";
  const numeric = Number(raw.replace(/[^\d.-]/g, ""));
  if (Number.isFinite(numeric)) {
    const formatted = numeric.toLocaleString("en-IN", { maximumFractionDigits: 2 });
    return suffix ? `${formatted} ${suffix}` : formatted;
  }
  return raw;
};

const isLongAmount = (value) => formatAmount(value).length > 16;

const statusClassName = (statusName) => {
  const s = String(statusName ?? "").toLowerCase();
  if (s.includes("approved") || s.includes("success")) return "ok";
  if (s.includes("pending")) return "pending";
  if (s.includes("rejected") || s.includes("failed") || s.includes("cancel")) return "bad";
  return "neutral";
};

/** Prefer ISO CreatedOn; fall back to AddTime string (UTC wall-clock from API). */
function statementInstant(item) {
  return item?.createdOn ?? item?.CreatedOn ?? item?.addTime ?? item?.AddTime ?? null;
}

function StatementDate({ item, compact = false }) {
  const raw = statementInstant(item);
  const { date, time, full } = formatUiDateTimeParts(raw);
  if (compact) {
    return (
      <time className="view-statement-date view-statement-date--compact" dateTime={raw || undefined} title={full}>
        {time ? `${date} · ${time}` : date}
      </time>
    );
  }
  return (
    <div className="view-statement-date" title={full}>
      <span>{date}</span>
      {time ? <small>{time}</small> : null}
    </div>
  );
}

function statementStatusLabel(statusName, t) {
  const s = String(statusName ?? "").toLowerCase();
  if (s.includes("approved")) return t("approved");
  if (s.includes("success")) return t("success");
  if (s.includes("pending")) return t("pending");
  if (s.includes("rejected")) return t("rejected");
  if (s.includes("failed")) return t("failed");
  if (s.includes("cancel")) return t("canceled");
  return statusName || "—";
}

function StatementStatus({ statusName }) {
  const { t } = useAppLanguage();
  return (
    <span
      className={`view-statement-status view-statement-status--${statusClassName(statusName)}`}
    >
      {statementStatusLabel(statusName, t)}
    </span>
  );
}

function StatementCard({ item, kind }) {
  const { t } = useAppLanguage();
  const isCredit = kind === "in";
  const remark = String(item.remark ?? "").trim();
  const amountText = formatAmount(item.amount);
  const longAmount = isLongAmount(item.amount);

  return (
    <article
      className={`view-statement-row view-statement-row--${isCredit ? "credit" : "debit"}`}
    >
      <div className="view-statement-row__accent" aria-hidden />
      <div className="view-statement-row__body">
        <div className="view-statement-row__main">
          <div className="view-statement-row__info">
            <StatementStatus statusName={item.statusName} />
            <StatementDate item={item} compact />
          </div>
          <div className="view-statement-row__amounts">
            <span
              className={`view-statement-row__amount${longAmount ? " is-long" : ""}`}
              title={String(item.amount ?? "")}
            >
              {isCredit ? "+" : "−"}
              {amountText}
            </span>
            <span className="view-statement-row__type">{isCredit ? t("credit") : t("debit")}</span>
          </div>
        </div>
        {remark ? <p className="view-statement-row__remark">{remark}</p> : null}
      </div>
    </article>
  );
}

function StatementTable({ items, kind }) {
  const { t } = useAppLanguage();
  const isCredit = kind === "in";

  return (
    <table className="view-statement-table">
      <thead>
        <tr>
          <th className="view-statement-col-index">#</th>
          <th>{t("status")}</th>
          <th className="view-statement-col-amount">{t("amount")}</th>
          <th>{t("date")}</th>
          <th>{t("remark")}</th>
          <th className="view-statement-col-flow">{t("type")}</th>
        </tr>
      </thead>
      <tbody>
        {items.map((item, index) => {
          const remark = String(item.remark ?? "").trim();
          return (
            <tr key={`${item.id ?? index}-${index}`}>
              <td className="view-statement-col-index">{index + 1}</td>
              <td>
                <StatementStatus statusName={item.statusName} />
              </td>
              <td
                className={`view-statement-amount view-statement-col-amount ${
                  isCredit ? "view-statement-cr" : "view-statement-dr"
                }`}
                title={String(item.amount ?? "")}
              >
                {formatAmount(item.amount)}
              </td>
              <td>
                <StatementDate item={item} />
              </td>
              <td className="view-statement-remark">{remark || "—"}</td>
              <td className={`view-statement-col-flow ${isCredit ? "view-statement-cr" : "view-statement-dr"}`}>
                <span className="view-statement-flow-badge view-statement-flow-badge--table">
                  {isCredit ? "CR" : "DR"}
                </span>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function StatementRecords({
  items,
  kind,
  embedded,
  loading,
  emptyMessage,
  onScroll,
}) {
  const { t } = useAppLanguage();
  const wrapClass = embedded
    ? "view-statement-list-wrap view-statement-list-wrap--cards"
    : "view-statement-table-wrap";

  return (
    <div className={wrapClass} onScroll={onScroll}>
      {loading && items.length === 0 && (
        <div className="view-statement-loading">{t("loading")}</div>
      )}

      {embedded ? (
        <div className="view-statement-cards">
          {items.length > 0 && (
            <div className="view-statement-list-head">
              <span className="view-statement-list-head__title">{t("transactions")}</span>
              <span className="view-statement-list-head__count">{items.length}</span>
            </div>
          )}
          <div className="view-statement-list">
            {items.map((item, index) => (
              <StatementCard key={`${item.id ?? index}-${index}`} item={item} kind={kind} />
            ))}
          </div>
        </div>
      ) : (
        <StatementTable items={items} kind={kind} />
      )}

      {loading && items.length > 0 && (
        <div className="view-statement-loading-inline">{t("loadingMore")}</div>
      )}
      {!loading && items.length === 0 && (
        <p className="view-statement-empty">{emptyMessage}</p>
      )}
    </div>
  );
}

export default function ViewStatementModal({ onClose, displayName, embedded = false }) {
  const dispatch = useDispatch();
  const { t } = useAppLanguage();
  const {
    payInList,
    payOutList,
    totalPayInRecord,
    totalPayOutRecord,
    payInOutListLoading,
  } = useSelector((s) => s.common);

  const [activeTab, setActiveTab] = useState("in");

  useEffect(() => {
    dispatch(getPayInOutList({ page: 1, payOut: "pay-in" }));
  }, [dispatch]);

  const loadMore = useCallback(
    (payOutKey) => {
      if (payInOutListLoading) return;
      if (payOutKey === "pay-in") {
        if (payInList.length >= totalPayInRecord) return;
        const page = Math.floor(payInList.length / 10) + 1;
        dispatch(getPayInOutList({ page, payOut: "pay-in" }));
      } else {
        if (payOutList.length >= totalPayOutRecord) return;
        const page = Math.floor(payOutList.length / 10) + 1;
        dispatch(getPayInOutList({ page, payOut: "pay-Out" }));
      }
    },
    [
      dispatch,
      payInOutListLoading,
      payInList.length,
      payOutList.length,
      totalPayInRecord,
      totalPayOutRecord,
    ]
  );

  const onScrollList = useCallback(
    (e, payOutKey) => {
      const el = e.target;
      if (el.scrollHeight - el.scrollTop - el.clientHeight < 80) {
        loadMore(payOutKey);
      }
    },
    [loadMore]
  );

  const switchTab = (tab) => {
    setActiveTab(tab);
    if (tab === "in") {
      dispatch(getPayInOutList({ page: 1, payOut: "pay-in" }));
    } else {
      dispatch(getPayInOutList({ page: 1, payOut: "pay-Out" }));
    }
  };

  const inner = (
    <>
      {!embedded && (
        <div className="modal-header view-statement-header">
          <div>
            <h3 id="view-statement-title">{t("viewStatement")}</h3>
            {displayName ? <p className="view-statement-sub">{displayName}</p> : null}
          </div>
          <button type="button" className="modal-close" onClick={onClose} aria-label={t("close")}>
            ✕
          </button>
        </div>
      )}

      <div className="view-statement-tabs">
        <button
          type="button"
          className={activeTab === "in" ? "active" : ""}
          onClick={() => switchTab("in")}
        >
          {t("addFunds")}
        </button>
        <button
          type="button"
          className={activeTab === "out" ? "active" : ""}
          onClick={() => switchTab("out")}
        >
          {t("withdrawFunds")}
        </button>
      </div>

      <div className="modal-body view-statement-body">
        {activeTab === "in" && (
          <StatementRecords
            items={payInList}
            kind="in"
            embedded={embedded}
            loading={payInOutListLoading}
            emptyMessage={t("noAddFundRecords")}
            onScroll={(e) => onScrollList(e, "pay-in")}
          />
        )}

        {activeTab === "out" && (
          <StatementRecords
            items={payOutList}
            kind="out"
            embedded={embedded}
            loading={payInOutListLoading}
            emptyMessage={t("noWithdrawRecords")}
            onScroll={(e) => onScrollList(e, "pay-out")}
          />
        )}
      </div>

      {!embedded && (
        <div className="modal-footer view-statement-footer">
          <button type="button" className="btn-confirm" onClick={onClose}>
            {t("close")}
          </button>
        </div>
      )}
    </>
  );

  if (embedded) {
    return (
      <div
        className="wallet-statement-embed"
        onClick={(e) => e.stopPropagation()}
        role="region"
        aria-label={t("statement")}
      >
        {inner}
      </div>
    );
  }

  return (
    <div className="modal-overlay view-statement-overlay" onClick={onClose}>
      <div
        className="modal-box view-statement-sheet"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="view-statement-title"
        aria-modal="true"
      >
        {inner}
      </div>
    </div>
  );
}
